import {
  Outlet,
  Link,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  ScriptOnce,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useRef, useState } from "react";

import appCss from "../styles.css?url";
import soraFontUrl from "@/assets/fonts/sora.woff2?url";
import manropeFontUrl from "@/assets/fonts/manrope.woff2?url";
import institutionalPublicCssUrl from "@/styles/institutional-public.css?url";
import institutionalSystemCssUrl from "@/styles/institutional-system-v2.css?url";
import institutionalComponentsCssUrl from "@/styles/institutional-page-components.css?url";
import institutionalLegibilityCssUrl from "@/styles/institutional-legibility-guardrails.css?url";
import institutionalReadabilityCssUrl from "@/styles/institutional-readability.css?url";
import resourceCodeCssUrl from "@/styles/resource-code.css?url";
import { getCriticalCss } from "@/generated/critical-css";
import { LocaleProvider } from "@/lib/i18n";
import {
  websiteJsonLd,
  organizationJsonLd,
  ROBOTS_INDEX,
  ROBOTS_NOINDEX_NOFOLLOW,
} from "@/lib/seo";
import { CookieBanner } from "@/components/site/CookieBanner";
import { OpenAIPixelTracker } from "@/components/site/OpenAIPixel";
import { GA4PageViews } from "@/components/site/GA4PageViews";

import { markNotFoundResponse } from "@/lib/cache-headers";
import {
  PUBLIC_THEME_STYLE_ID,
  preparePublicThemeCss,
  readPublicThemeCss,
} from "@/lib/public-theme-css";

// sonner is ~66 KB and is only needed once a toast is actually shown.
// Load it after the initial page has settled instead of on the first pointer or
// scroll event; importing a UI library during an interaction can inflate INP
// and competes with late LCP candidates on slower desktop connections.
const LazyToaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

function DeferredToaster() {
  const [mount, setMount] = useState(false);
  const loc = useLocation();
  const initialPath = useRef(loc.pathname);

  useEffect(() => {
    if (mount || typeof window === "undefined") return;
    let timer = 0;
    let idleId: number | null = null;
    const schedule = () => {
      const requestIdle = window.requestIdleCallback?.bind(window);
      if (requestIdle) {
        idleId = requestIdle(() => setMount(true), { timeout: 4000 });
      } else {
        timer = window.setTimeout(() => setMount(true), 2500);
      }
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      if (timer) window.clearTimeout(timer);
      if (idleId !== null) window.cancelIdleCallback?.(idleId);
    };
  }, [mount]);

  // A client-side navigation is already outside the initial LCP window.
  useEffect(() => {
    if (!mount && loc.pathname !== initialPath.current) setMount(true);
  }, [loc.pathname, mount]);

  if (!mount) return null;
  return (
    <Suspense fallback={null}>
      <LazyToaster />
    </Suspense>
  );
}

function NotFoundComponent() {
  // Server-side: emit HTTP 404 + X-Robots-Tag: noindex so crawlers don't
  // index soft-404s. Client-side: no-op.
  markNotFoundResponse();

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isEs = pathname.startsWith("/es");

  const copy = isEs
    ? {
        eyebrow: "Error 404",
        title: "Página no encontrada",
        body: "La URL que buscas no existe o fue movida. Prueba con uno de estos destinos populares:",
        home: "Ir al inicio",
        sections: [
          {
            heading: "Servicios",
            links: [
              { to: "/es/servicios", label: "Servicios Shopify" },
              { to: "/es/cro-shopify", label: "CRO para Shopify" },
              { to: "/es/rendimiento", label: "Rendimiento & Core Web Vitals" },
              { to: "/es/desarrollo-shopify", label: "Desarrollo Shopify" },
            ],
          },
          {
            heading: "Recursos",
            links: [
              { to: "/es/recursos", label: "Biblioteca de recursos" },
              { to: "/es/proyectos", label: "Casos de estudio" },
              { to: "/es/auditoria", label: "Auditoría gratuita" },
              { to: "/es/cotizacion", label: "Solicitar cotización" },
            ],
          },
        ],
      }
    : {
        eyebrow: "Error 404",
        title: "Page not found",
        body: "The URL you're looking for doesn't exist or has been moved. Try one of these popular destinations:",
        home: "Go home",
        sections: [
          {
            heading: "Services",
            links: [
              { to: "/services", label: "Shopify services" },
              { to: "/shopify-cro", label: "Shopify CRO" },
              { to: "/shopify-performance", label: "Performance & Core Web Vitals" },
              { to: "/shopify-experts", label: "Shopify development" },
            ],
          },
          {
            heading: "Resources",
            links: [
              { to: "/resources", label: "Resource library" },
              { to: "/work", label: "Case studies" },
              { to: "/audit", label: "Free audit" },
              { to: "/get-a-quote", label: "Get a quote" },
            ],
          },
        ],
      };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-16">
      {/*
        No in-body robots meta here: the root `head()` already resolves the
        single `<meta name="robots" content="noindex, nofollow">` from the
        not-found match status, and `markNotFoundResponse()` sets the 404
        status + `X-Robots-Tag` header. One effective directive, no conflict.
      */}
      <div className="w-full max-w-2xl text-center">
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">
          {copy.eyebrow}
        </p>
        <h1 className="mt-4 font-display text-5xl font-semibold tracking-tight text-foreground">
          {copy.title}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">{copy.body}</p>
        <div className="mt-8">
          <Link
            to={isEs ? "/es" : "/"}
            className="inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-colors hover:bg-foreground/90"
          >
            {copy.home}
          </Link>
        </div>
        <nav
          aria-label={isEs ? "Enlaces útiles" : "Helpful links"}
          className="mt-12 grid gap-8 text-left sm:grid-cols-2"
        >
          {copy.sections.map((section) => (
            <div key={section.heading}>
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {section.heading}
              </h2>
              <ul className="mt-3 space-y-2">
                {section.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-foreground underline-offset-4 hover:underline"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0a0a0a" },
      { title: "Shugert: Premium Shopify Plus Agency · USA & Latam" },
      {
        name: "description",
        content:
          "Shopify Select Partner since 2015. Offices in Hermosillo, Barcelona, and New York. We design, build, and grow DTC brands across USA and Latam.",
      },
      { name: "author", content: "Shugert Agency" },
      // Site-wide default robots directive. `buildHead` routes override this by
      // `name` de-dupe; a not-found match flips it to noindex here so a 404
      // never ships a conflicting pair of robots tags.
      {
        name: "robots",
        content: matches.some(
          (m) => m.status === "notFound" || (m as { globalNotFound?: boolean }).globalNotFound,
        )
          ? ROBOTS_NOINDEX_NOFOLLOW
          : ROBOTS_INDEX,
      },
      {
        name: "google-site-verification",
        content: "5_3cH2iK_mdTHFExD4LBlGiMFfOo4qSsLgwExYa5CLo",
      },
      {
        name: "msvalidate.01",
        content: "6C4A4AF352AC3D73D75BB591BD6D0654",
      },
      { property: "og:site_name", content: "Shugert" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "es_MX" },
      { property: "og:title", content: "Shugert: Premium Shopify Plus Agency · USA & Latam" },
      {
        property: "og:description",
        content:
          "Shopify Select Partner since 2015. Offices in Hermosillo, Barcelona, and New York. We design, build, and grow DTC brands across USA and Latam.",
      },
      { property: "og:url", content: "https://shugert.com.mx/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@Shugert" },
      { name: "twitter:title", content: "Shugert: Premium Shopify Plus Agency · USA & Latam" },
      {
        name: "twitter:description",
        content:
          "Shopify Select Partner since 2015. Offices in Hermosillo, Barcelona, and New York. We design, build, and grow DTC brands across USA and Latam.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteJsonLd("en")),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(organizationJsonLd()),
      },
      {
        id: "accessibe",
        // Accessibility tooling remains available, but it no longer starts on
        // the first scroll/pointer event while LCP may still be unresolved.
        children: `(function(){var loaded=false,delay=0;function load(){if(loaded)return;loaded=true;if(document.getElementById('acsb-loaded'))return;var s=document.createElement('script');s.id='acsb-loaded';var h=document.querySelector('head')||document.body;s.src='https://acsbapp.com/apps/app/dist/js/app.js';s.async=true;s.onload=function(){acsbJS.init({statementLink:'https://dashboard.accessibe.com/app/statement?license=6453c13e04bd1e000347edc7',footerHtml:'',hideMobile:false,hideTrigger:false,disableBgProcess:false,language:'en',position:'right',leadColor:'#146FF8',triggerColor:'#146FF8',triggerRadius:'50%',triggerPositionX:'right',triggerPositionY:'bottom',triggerIcon:'people',triggerSize:'bottom',triggerOffsetX:20,triggerOffsetY:20,mobile:{triggerSize:'small',triggerPositionX:'right',triggerPositionY:'bottom',triggerOffsetX:10,triggerOffsetY:10,triggerRadius:'20'}});};h.appendChild(s);}function idle(){if(loaded)return;if('requestIdleCallback'in window){requestIdleCallback(load,{timeout:5000});}else{setTimeout(load,500);}}function schedule(){if(loaded||delay)return;delay=setTimeout(function(){delay=0;idle();},15000);}function prioritizeForKeyboard(){if(loaded)return;if(delay){clearTimeout(delay);delay=0;}idle();}addEventListener('keydown',prioritizeForKeyboard,{once:true});if(document.readyState==='complete')schedule();else addEventListener('load',schedule,{once:true});})();`,
      },
      {
        id: "gtag-loader",
        children: `(function(){window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;function hasConsent(){try{var c=window.__consent;if(c&&typeof c.analytics==='boolean')return c.analytics;var raw=localStorage.getItem('consent.v2');if(raw)return !!JSON.parse(raw).analytics;var legacy=localStorage.getItem('consent.v1');if(!legacy)return false;return !!JSON.parse(legacy).analytics;}catch(e){return false;}}var loaded=false;function load(){if(loaded||!hasConsent())return;loaded=true;gtag('js',new Date());gtag('config','G-LHD9X6Y8TP',{anonymize_ip:true,send_page_view:false});var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-LHD9X6Y8TP';(document.head||document.body).appendChild(s);}window.addEventListener('consent:change',load);if(document.readyState==='complete')load();else addEventListener('load',load,{once:true});})();`,
      },
    ],
    links: [
      // The compiled design-system stylesheet itself is rendered directly in
      // `RootShell` below (as a preload-then-swap link), not through this
      // typed `links` array — `HeadContent` silently dropped the entry here
      // when it carried an `onload` attribute it didn't recognize, so full
      // JSX control was the reliable option.
      { rel: "icon", href: "/brand/shugert-sh-mark-lime.svg", type: "image/svg+xml" },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://acsbapp.com" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: soraFontUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: manropeFontUrl,
        crossOrigin: "anonymous",
      },
    ],
  }),
  // Runs in parallel with the child route loaders, so the admin theme CSS is
  // ready to inline into the shell without an extra blocking request.
  loader: async () => {
    await preparePublicThemeCss();
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: RootErrorComponent,
});

function RootErrorComponent({ error }: { error: Error }) {
  if (typeof console !== "undefined") {
    console.error("[root-error]", error?.stack ?? error?.message ?? error);
  }
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">
          Error
        </p>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-foreground">
          Something went wrong
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          An unexpected error occurred. Please refresh the page.
        </p>
        <div className="mt-6">
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-colors hover:bg-foreground/90"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const themeScript = `(function(){try{document.documentElement.classList.remove('dark');document.documentElement.style.colorScheme='light';}catch(e){}})();`;

// Baseline shell reset, always inlined regardless of route: prevents a
// white/dark flash before either the per-route critical CSS bucket or the
// full stylesheet has painted anything.
const SHELL_RESET_CSS = `:root{color-scheme:light}html,body{margin:0;background:#ffffff;color:#0a0a0a;font-family:Manrope,"Manrope Fallback",system-ui,-apple-system,Segoe UI,Roboto,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}body{min-height:100vh}`;
// Metric-matched fallbacks (Arial scaled to Sora/Manrope advance widths and
// vertical metrics) so headings wrap to the same number of lines before and
// after the web fonts swap in. They sit after the real families in the
// stacks and cover Latin-1 only (both web fonts ship every Latin-1 glyph),
// so rendering after the swap is unchanged.
const FONT_FACE_CSS = `@font-face{font-family:"Sora";font-style:normal;font-weight:600 700;font-display:swap;src:url("${soraFontUrl}") format("woff2")}@font-face{font-family:"Manrope";font-style:normal;font-weight:400 600;font-display:swap;src:url("${manropeFontUrl}") format("woff2")}@font-face{font-family:"Sora Fallback";src:local("Arial Bold"),local("Arial-BoldMT"),local("Arial");font-weight:600 700;size-adjust:106.66%;ascent-override:90.94%;descent-override:27.19%;line-gap-override:0%;unicode-range:U+0000-00FF}@font-face{font-family:"Manrope Fallback";src:local("Arial"),local("ArialMT");font-weight:400 600;size-adjust:102.9%;ascent-override:103.99%;descent-override:29.16%;line-gap-override:0%;unicode-range:U+0000-00FF}`;
const RESOURCE_ARTICLE_CRITICAL_CSS_URL = "/critical/pillar-article-20260904.css";
const PUBLIC_STYLESHEET_URLS = [
  institutionalPublicCssUrl,
  institutionalSystemCssUrl,
  institutionalComponentsCssUrl,
  institutionalLegibilityCssUrl,
  institutionalReadabilityCssUrl,
];

function RootShell({ children }: { children: React.ReactNode }) {
  // Match `<html lang>` to the request locale on the SSR shell so /es/*
  // pages don't ship with lang="en" (fixes hreflang/HTML-lang mismatch).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = pathname.startsWith("/es") ? "es-MX" : "en";
  // The design-system stylesheets are render-blocking on purpose. They used
  // to be preloaded and swapped to rel="stylesheet" from a React effect,
  // which only ran after hydration: first paint used the truncated critical
  // CSS below, then the whole layout reflowed once JS executed. Search
  // Console reported that as CLS > 0.25 and LCP > 4s on every public
  // template (a hero ::after pseudo-element alone scored CLS 1.0). The
  // sheets are hashed, immutable and same-origin, so blocking on them costs
  // one parallel fetch and guarantees the first paint is the final layout.
  //
  // The `shugert-critical` layer is still shipped even though it no longer
  // gates first paint: it carries `!important` declarations, and important
  // declarations in an earlier layer beat unlayered ones, so part of the
  // current look (e.g. footer link ink on dark surfaces) depends on it.
  // Dropping it would visibly change the site.
  const isResourceArticle = /^\/(?:resources|es\/recursos|blog|es\/blog)\//.test(pathname);
  const criticalCss =
    SHELL_RESET_CSS +
    FONT_FACE_CSS +
    (isResourceArticle
      ? "@layer shugert-critical;"
      : `@layer shugert-critical{${getCriticalCss(pathname)}}`);
  const usesDynamicTheme =
    !/^\/(?:admin|portal|agency|agency-login|partner-login|login|reset-password|checkout|order-confirmed)(?:\/|$)/.test(
      pathname,
    );
  // Admin theme CSS, inlined last so it keeps the cascade position the
  // `/theme.css` link had. Falls back to that link if it could not be built.
  const themeCss = usesDynamicTheme ? readPublicThemeCss() : null;
  const usesResourceCodeStyles = /^\/(?:resources|es\/recursos|blog|es\/blog)(?:\/|$)/.test(
    pathname,
  );

  return (
    <html lang={lang} style={{ colorScheme: "light" }} suppressHydrationWarning>
      <head>
        <style dangerouslySetInnerHTML={{ __html: criticalCss }} />
        <ScriptOnce>{themeScript}</ScriptOnce>
        {isResourceArticle ? (
          <link
            rel="stylesheet"
            href={RESOURCE_ARTICLE_CRITICAL_CSS_URL}
            data-critical-css="resource-article"
          />
        ) : null}
        <link rel="stylesheet" href={appCss} />
        {usesDynamicTheme
          ? PUBLIC_STYLESHEET_URLS.map((href) => <link key={href} rel="stylesheet" href={href} />)
          : null}
        {usesResourceCodeStyles ? <link rel="stylesheet" href={resourceCodeCssUrl} /> : null}
        <HeadContent />
        {usesDynamicTheme ? (
          themeCss !== null ? (
            <style
              id={PUBLIC_THEME_STYLE_ID}
              data-dynamic-theme="shugert"
              dangerouslySetInnerHTML={{ __html: themeCss }}
            />
          ) : (
            <link rel="stylesheet" href="/theme.css" data-dynamic-theme="shugert" />
          )
        ) : null}
      </head>
      <body className="bg-background text-foreground antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function LangSync() {
  const loc = useLocation();
  const lang = loc.pathname.startsWith("/es") ? "es-MX" : "en";
  if (typeof document !== "undefined" && document.documentElement.lang !== lang) {
    document.documentElement.lang = lang;
  }
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <LangSync />
        <Outlet />
        <CookieBanner />
        <GA4PageViews />
        <OpenAIPixelTracker />
        <DeferredToaster />
      </LocaleProvider>
    </QueryClientProvider>
  );
}
